/**
 * AttachmentsField — shared drag-and-drop + click-to-upload file picker.
 *
 * Extracted from GetVerifiedPage so both that page (buyer submission) and
 * VerdictSubmitPage (expert verdict attachments) can reuse one component.
 *
 * Each file uploads to /api/upload/artifact individually so we can show
 * per-file progress + per-file errors (one file failing shouldn't kill
 * the other uploads). Successful uploads append to the `attachments`
 * array (shape: {name, url, content_type, size, ext}); the parent's
 * submit handler passes the array through to the backend.
 *
 * Props:
 *   - attachments: current array
 *   - setAttachments: setter (called with new array)
 *   - label, helperText: copy override (defaults sensible for buyer flow)
 *   - maxAttachments: defaults to 10 (matches backend cap)
 */

import React, { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";

const DEFAULT_MAX_ATTACHMENTS = 10;
const ALLOWED_EXTS_DISPLAY = ".pdf, .docx, .txt, .md, .csv, .json, " +
                             ".js, .ts, .py, .html, .css, .yaml, " +
                             ".png, .jpg, .webp, .zip";
const ALLOWED_MIME_RE = /^(application\/(pdf|msword|json|javascript|zip|x-yaml|vnd\.openxmlformats-officedocument\.wordprocessingml\.document)|text\/.*|image\/(png|jpeg|webp|gif))$/i;

export default function AttachmentsField({
  attachments,
  setAttachments,
  label = "Attach files (optional)",
  helperText,
  maxAttachments = DEFAULT_MAX_ATTACHMENTS,
}) {
  const [dragHot, setDragHot] = useState(false);
  const [uploading, setUploading] = useState([]);   // [{tmpId, name, pct, err}]
  const inputRef = useRef(null);

  const remainingSlots = Math.max(0, maxAttachments - (attachments.length + uploading.length));

  const onFilesPicked = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    if (files.length > remainingSlots) {
      toast.error(`You can attach at most ${maxAttachments} files. Skipping the extras.`);
    }
    const accepted = files.slice(0, remainingSlots);

    const okFiles = [];
    for (const f of accepted) {
      if (f.size > 25 * 1024 * 1024) {
        toast.error(`"${f.name}" is over 25 MB — skipped.`);
        continue;
      }
      const ct = f.type || "";
      if (!ALLOWED_MIME_RE.test(ct) && !ct.startsWith("text/")) {
        if (ct && ct !== "") {
          toast.error(`"${f.name}" (${ct}) isn't a supported file type — skipped.`);
          continue;
        }
      }
      okFiles.push(f);
    }
    if (!okFiles.length) return;

    for (const f of okFiles) {
      const tmpId = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      setUploading((u) => [...u, { tmpId, name: f.name, pct: 0, err: null }]);

      const form = new FormData();
      form.append("file", f);

      axios.post(`${API}/upload/artifact`, form, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          const pct = e.total ? Math.round((e.loaded / e.total) * 100) : 0;
          setUploading((u) => u.map((x) => x.tmpId === tmpId ? { ...x, pct } : x));
        },
      })
        .then((resp) => {
          setUploading((u) => u.filter((x) => x.tmpId !== tmpId));
          setAttachments((a) => [...a, resp.data]);
          toast.success(`Attached: ${resp.data.name}`);
        })
        .catch((err) => {
          const detail = err?.response?.data?.detail || err?.message || "Upload failed.";
          setUploading((u) => u.map((x) => x.tmpId === tmpId ? { ...x, err: detail, pct: 100 } : x));
          toast.error(`Couldn't attach "${f.name}": ${detail}`);
          setTimeout(() => {
            setUploading((u) => u.filter((x) => x.tmpId !== tmpId));
          }, 4000);
        });
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragHot(false);
    onFilesPicked(e.dataTransfer?.files);
  };

  const removeAttachment = (url) => {
    setAttachments((a) => a.filter((x) => x.url !== url));
  };

  const effectiveHelper = helperText
    || `Add PDFs, screenshots, code files, or any artifact for the expert. Up to ${maxAttachments} files, 25 MB each.`;

  return (
    <div>
      <label className="text-xs text-slateline-500 tracking-wider uppercase font-semibold">
        {label}
      </label>
      <p className="text-xs text-slateline-400 mt-1">{effectiveHelper}</p>

      {/* Drop zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragHot(true); }}
        onDragLeave={() => setDragHot(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") inputRef.current?.click(); }}
        className={[
          "mt-2 cursor-pointer rounded-md border-2 border-dashed p-5 transition-colors",
          "text-center text-sm select-none",
          dragHot
            ? "border-brand-green bg-brand-greenPale/40 text-ink"
            : "border-slateline-300 bg-canvas-card text-slateline-500 hover:border-ink/40 hover:text-ink",
        ].join(" ")}
      >
        <p className="font-medium">
          {dragHot ? "Drop to attach" : "Drag files here or click to choose"}
        </p>
        <p className="text-[11px] text-slateline-400 mt-1">{ALLOWED_EXTS_DISPLAY}</p>
      </div>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept=".pdf,.docx,.doc,.txt,.md,.csv,.json,.js,.jsx,.ts,.tsx,.py,.html,.css,.yaml,.yml,.png,.jpg,.jpeg,.webp,.gif,.zip"
        className="hidden"
        onChange={(e) => {
          onFilesPicked(e.target.files);
          e.target.value = "";
        }}
      />

      {/* Uploading rows */}
      {uploading.length > 0 && (
        <ul className="mt-3 space-y-1.5">
          {uploading.map((u) => (
            <li key={u.tmpId}
                className="flex items-center gap-3 px-3 py-2 rounded-md border border-slateline-200 bg-canvas-card text-sm">
              <span className="flex-1 truncate text-ink">{u.name}</span>
              {u.err
                ? <span className="text-rose-600 text-xs">{u.err}</span>
                : <span className="text-slateline-500 text-xs">{u.pct}%</span>}
            </li>
          ))}
        </ul>
      )}

      {/* Attached rows */}
      {attachments.length > 0 && (
        <ul className="mt-3 space-y-1.5">
          {attachments.map((a) => (
            <li key={a.url}
                className="flex items-center gap-3 px-3 py-2 rounded-md border border-slateline-200 bg-canvas-card text-sm">
              <span className="text-brand-green flex-shrink-0" aria-hidden>
                {a.ext === "pdf" ? "📄"
                 : ["png","jpg","jpeg","webp","gif"].includes(a.ext) ? "🖼"
                 : ["js","ts","py","html","css","json","yaml","md"].includes(a.ext) ? "💻"
                 : "📎"}
              </span>
              <span className="flex-1 truncate text-ink">{a.name}</span>
              <span className="text-[11px] text-slateline-400 flex-shrink-0">
                {a.size ? `${Math.max(1, Math.round(a.size / 1024))} KB` : ""}
              </span>
              <button
                type="button"
                onClick={() => removeAttachment(a.url)}
                aria-label={`Remove ${a.name}`}
                className="text-slateline-400 hover:text-rose-600 text-lg leading-none flex-shrink-0"
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
